import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { OauthService } from './oauth.service';
import { Rule, CDSNode} from './capd.model';
import { APIURL } from './url.config';
import { throwError } from "rxjs";
import { SecureTokenService } from './secure-token.service';

/**
 * Class that gets lists of users, lists of rules assigned to users, and modifies settings
 * for selected users.
 *
 */
@Injectable()
export class UserService {

  /** Creates the HTTP object and Oauth object to be shared throughout the service. */
  constructor(private http: HttpClient,
              private auth: OauthService) { }

  /**
   * Gets a list of users for a given company.
   *
   * @async
   * @func getUsers
   * @param {string} root - the root ID of the CDS node whose users are being retrieved.
   * @param {string} group - the ID of the CDS group whose users are being retrieved
   * @param {any} token - The access token required to make the service call
   * @return {Promise} a list of users.
   */
  getUsers(root: string, group: string) {
    return this.auth.getToken().then(async token => {
      const url = APIURL + 'api/cdstree/nodes/' + root + '%2B' + group + '/users';
      const authorization = 'bearer ' + token;
      let myHeaders = new HttpHeaders({
        Authorization: authorization,
        "auth": "bearer " + SecureTokenService.getAccessToken()
      });
      const options = {headers : myHeaders};

      return this.http.get<CDSNode[]>(url, options)
        .toPromise()
        .catch(this.handleError);
    })
    .catch(e => {
      this.handleError
      return e;
    });
  }

  /**
   * Gets a list of rules for a given user.
   *
   * @async
   * @func getUserRules
   * @param {string} org - the root ID of the organization the users belong to.
   * @param {string} user - the ID of the user whose rules you're retrieving.
   * @returns {Promise<Rule[]>} a list of rules.
   */
  getUserRules(org: string, user: string, groupShortOids: string[]) {
    user = user.replace("2.16.840.1.113883.3.21", "~")
    let groupIds = [];
    groupShortOids.forEach(oid => {
      let id = oid.replace("2.16.840.1.113883.3.21", "~")
      groupIds.push(id)
    })
    return this.auth.getToken().then(token => {
      const url = APIURL + 'api/contentpackages/orgs/' + org + '/users/' + user + '/rules';
      const authorization = 'bearer ' + token;
      const options = {headers : new HttpHeaders(
            { Authorization: authorization, 'Content-Type': 'application/json' , 'Group-Oids': groupIds}
      )};

      return this.http.get<Rule[]>(url, options)
        .toPromise()
        .catch(this.handleError);
    })
    .catch(this.handleError);
  }

  getRulesForUserByGroups(groupShortOids: string[]) {
    let groupIds = [];
    groupShortOids.forEach(oid => {
      let id = oid.replace("2.16.840.1.113883.3.21", "~")
      groupIds.push(id)
    })
    return this.auth.getToken().then(token => {
      const url = APIURL + 'api/groups/v2/content';
      const authorization = 'bearer ' + token;
      const options = {headers : new HttpHeaders(
            { Authorization: authorization, 'Content-Type': 'application/json' , 'groupOids': groupIds}
        )};

      return this.http.get<Rule[]>(url, options)
          .toPromise()
          .catch(this.handleError);
    })
        .catch(this.handleError);
  }

  /**
   * Updates a list of user rules with a new status specified by argument.
   *
   * @async
   * @func updateUserRules
   * @param {string} org - the root ID of the organization the user belongs to.
   * @param {string} user - the ID of the user whose rules you're retrieving.
   * @param {Rule[]} ruleEdits - the list of rules with the new statuses applied.
   */
  updateUserRules(org: string, user: string, ruleEdits: Rule[]) {
    user = user.replace("2.16.840.1.113883.3.21", "~")
    return this.auth.getToken().then(token => {
      const url = APIURL + 'api/contentpackages/orgs/' + org + '/users/' + user + '/rules';
      const authorization = 'bearer ' + token;
      const options = {headers : new HttpHeaders(
        { Authorization: authorization, 'Content-Type': 'application/json' }
      )};
      const body = JSON.stringify(ruleEdits);

      return this.http.post(url, body, options)
        .toPromise()
        .catch(this.handleError);
    })
    .catch(this.handleError);
  }

  getModifiedUsersByOrg(org: string) {
    return this.auth.getToken().then(token => {
      const url = APIURL + 'api/contentpackages/orgs/' + org + '/users/modified';
      const authorization = 'bearer ' + token;
      const options = {headers : new HttpHeaders(
            { Authorization: authorization, 'Content-Type': 'application/json' }
        )};

      return this.http.get<string[]>(url, options)
          .toPromise()
          .catch(this.handleError);
    })
        .catch(this.handleError);
  }

  /**
   * Handles errors by throwing them to other functions.
   */
  private handleError(error: Response) {
    console.error(error);
    return throwError(error || 'Server error');
  }
}
